import { css } from '@emotion/css';
import { Box } from '@mui/material';
import {
  EdgeLabelRenderer,
  useReactFlow,
  type XYPosition,
} from '@xyflow/react';
import { type PointerEvent, useState } from 'react';

import { EdgeLabel } from '@/components/diagrams/components/EdgeLabel';
import { InlineTextEditor } from '@/components/diagrams/components/InlineTextEditor/InlineTextEditor';
import { DiagramEdgeRoute } from '@/components/diagrams/diagramStyle';
import {
  ArrowEdge,
  type ArrowEdgeProps,
} from '@/components/diagrams/FlowChart/edges/ArrowEdge';
import { getOrthogonalPathLabelPoint } from '@/components/diagrams/FlowChart/edges/orthogonalPath';
import {
  getNearestFraction,
  getPointAtFraction,
} from '@/components/diagrams/FlowChart/edges/pathFraction';
import {
  getRelationshipLabelSize,
  RELATIONSHIP_NODE_WIDTH,
} from '@/components/diagrams/FlowChart/nodes/RelationshipLabelNode/relationshipLabelSize';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import {
  dragLineHandle,
  getElbowRoute,
  getLineHandles,
  type LineLabelBox,
} from '../../utils/diagramLineGeometry';
import { type ReactFlowDiagramEdgeData } from '../../utils/reactFlowDiagram';
import { LineControlPoint } from './LineControlPoint';

/** A label has the width of the relationship labels of the other diagrams. */
const LABEL_WIDTH = RELATIONSHIP_NODE_WIDTH;
const LABEL_CLASSES = { root: css({ width: LABEL_WIDTH }) };
/** A dragged label stays this fraction away from the ends of its line. */
const LABEL_END_MARGIN = 0.05;
/** The cursor over a handle: the direction that the handle moves. */
const HANDLE_CURSOR = {
  x: 'ew-resize',
  y: 'ns-resize',
  add: 'copy',
  bend: 'move',
};

/** A drag in progress: a route handle or the label, with its live result. */
type LineDrag =
  | { kind: 'route'; waypoints: XYPosition[] }
  | { kind: 'label'; fraction: number };

/**
 * Starts a drag on a pointer down. `onMove` returns the live result of each
 * pointer move, in canvas pixels. `onEnd` gets the last result when the user
 * releases the pointer.
 */
type StartLineDrag = (
  onMove: (point: XYPosition) => LineDrag,
  onEnd: (drag: LineDrag) => void
) => (event: PointerEvent<HTMLElement>) => void;

/**
 * A builder line: the shared arrow, with route handles and a label that the
 * user drags along the line while the line is selected. A double-click on
 * the label edits it in place. A drag saves its result as one edit when the
 * user releases the pointer.
 *
 * The shared arrow gives its path a new key at each render, so a render
 * between a pointer down and its click would lose the click that selects the
 * line. Only the label and the handles read the editing context, so the
 * line renders again only when React Flow changes it or during a drag.
 */
export function DiagramLineEdge(props: ArrowEdgeProps) {
  const { id, data, selected } = props;
  const { screenToFlowPosition } = useReactFlow();
  const [drag, setDrag] = useState<LineDrag | null>(null);
  const { label, labelFraction } = data as ReactFlowDiagramEdgeData;

  const route = drag?.kind === 'route' ? drag.waypoints : data.waypoints;
  const isElbow =
    (data.route ?? DiagramEdgeRoute.Elbow) === DiagramEdgeRoute.Elbow;
  const ends = {
    source: { x: props.sourceX, y: props.sourceY },
    sourceSide: props.sourcePosition,
    target: { x: props.targetX, y: props.targetY },
    targetSide: props.targetPosition,
  };
  // The points of the drawn line. An elbow line without saved bends follows
  // the automatic layout route, when it has one.
  let points: XYPosition[];
  if (isElbow && !route?.length && data.routeWaypoints?.length) {
    points = [ends.source, ...data.routeWaypoints, ends.target];
  } else if (isElbow) {
    points = getElbowRoute(ends, route ?? []);
  } else {
    points = [ends.source, ...(route ?? []), ends.target];
  }
  const shownFraction =
    drag?.kind === 'label' ? drag.fraction : (labelFraction ?? undefined);
  const labelPosition =
    shownFraction === undefined
      ? data.labelPosition
      : getPointAtFraction(points, shownFraction);
  // Without a set position, the shared arrow draws the label at the middle
  // of the longest segment. A curved line without bends draws it on the
  // curve, near that point.
  const labelBox: LineLabelBox | undefined = label
    ? {
        center: labelPosition ?? getOrthogonalPathLabelPoint(points),
        ...getRelationshipLabelSize(label),
      }
    : undefined;

  const startDrag: StartLineDrag = (onMove, onEnd) => (event) => {
    event.stopPropagation();
    const element = event.currentTarget;
    element.setPointerCapture(event.pointerId);
    let last: LineDrag | null = null;
    const move = (moveEvent: globalThis.PointerEvent) => {
      last = onMove(
        screenToFlowPosition({ x: moveEvent.clientX, y: moveEvent.clientY })
      );
      setDrag(last);
    };
    const end = () => {
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerup', end);
      setDrag(null);
      if (last) onEnd(last);
    };
    element.addEventListener('pointermove', move);
    element.addEventListener('pointerup', end);
  };

  return (
    <>
      <ArrowEdge
        {...props}
        data={{
          ...data,
          waypoints: route?.length ? route : data.waypoints,
          edgeLabel: (
            <DiagramLineLabel
              edgeID={id}
              label={label}
              selected={!!selected}
              points={points}
              startDrag={startDrag}
            />
          ),
          labelPosition,
        }}
      />
      {selected && (
        <DiagramLineHandles
          edgeID={id}
          points={points}
          isElbow={isElbow}
          labelBox={labelBox}
          startDrag={startDrag}
        />
      )}
    </>
  );
}

interface DiagramLinePartProps {
  edgeID: string;
  points: XYPosition[];
  startDrag: StartLineDrag;
}

/**
 * The label of a line. The user drags the label of a selected line along the
 * line, and double-clicks it to edit it in place. A line without a label
 * shows the editor only after the user selects Add label.
 */
function DiagramLineLabel({
  edgeID,
  label,
  selected,
  points,
  startDrag,
}: DiagramLinePartProps & { label: string; selected: boolean }) {
  const { locked, editEdge, labelTarget, openLabelEditor, closeLabelEditor } =
    useDiagramEditing();
  if (locked)
    return label ? <EdgeLabel label={label} classes={LABEL_CLASSES} /> : null;

  if (labelTarget?.kind === 'edge' && labelTarget.id === edgeID) {
    return (
      <Box sx={{ pointerEvents: 'all' }}>
        <EdgeLabel
          classes={LABEL_CLASSES}
          highlight
          editor={
            <InlineTextEditor
              value={label}
              label="Label"
              onCommit={(text) => {
                closeLabelEditor();
                // A line without label text shows no label.
                if (text.trim() !== label) {
                  editEdge(edgeID, { label: text.trim() });
                }
              }}
              onCancel={closeLabelEditor}
            />
          }
        />
      </Box>
    );
  }
  if (!label) return null;
  return (
    <Box
      sx={{ pointerEvents: 'all', cursor: selected ? 'grab' : undefined }}
      onDoubleClick={() => openLabelEditor({ kind: 'edge', id: edgeID })}
      onPointerDown={
        selected
          ? startDrag(
              (point) => ({
                kind: 'label',
                fraction: Math.min(
                  1 - LABEL_END_MARGIN,
                  Math.max(LABEL_END_MARGIN, getNearestFraction(points, point))
                ),
              }),
              (done) =>
                done.kind === 'label' &&
                editEdge(edgeID, {
                  labelPosition: Number(done.fraction.toFixed(3)),
                })
            )
          : undefined
      }
    >
      <EdgeLabel label={label} classes={LABEL_CLASSES} />
    </Box>
  );
}

/**
 * The route handles of a selected line. An elbow line has a handle on each
 * inner segment. A straight or curved line has a handle in the middle of
 * each segment, which adds a bend, and a handle on each bend. The line has
 * no handles while the user edits its label. A handle does not cover the
 * label, so the user can read the label and double-click it.
 */
function DiagramLineHandles({
  edgeID,
  points,
  isElbow,
  labelBox,
  startDrag,
}: DiagramLinePartProps & { isElbow: boolean; labelBox?: LineLabelBox }) {
  const { locked, editEdge, labelTarget } = useDiagramEditing();
  if (locked || (labelTarget?.kind === 'edge' && labelTarget.id === edgeID)) {
    return null;
  }
  const saveRoute = (done: LineDrag) =>
    done.kind === 'route' && editEdge(edgeID, { waypoints: done.waypoints });
  // The bends of a straight or curved line are its saved waypoints.
  const bends = isElbow ? [] : points.slice(1, -1);
  const moveBend = (index: number, to: XYPosition) =>
    bends.map((bend, each) =>
      each === index ? { x: Math.round(to.x), y: Math.round(to.y) } : bend
    );
  return (
    <EdgeLabelRenderer>
      {getLineHandles(points, isElbow, labelBox).map((handle) => (
        <LineControlPoint
          key={`${handle.index}-${handle.axis ?? 'add'}`}
          point={handle.point}
          active={false}
          label={handle.axis ? 'Move line segment' : 'Add a bend'}
          cursor={HANDLE_CURSOR[handle.axis ?? 'add']}
          onPointerDown={startDrag(
            (point) => ({
              kind: 'route',
              waypoints: dragLineHandle(points, handle, point),
            }),
            saveRoute
          )}
        />
      ))}
      {bends.map((bend, index) => (
        <LineControlPoint
          key={`bend-${index}`}
          point={bend}
          active
          label="Move bend"
          cursor={HANDLE_CURSOR.bend}
          onPointerDown={startDrag(
            (point) => ({ kind: 'route', waypoints: moveBend(index, point) }),
            saveRoute
          )}
          onMove={(step) =>
            editEdge(edgeID, {
              waypoints: moveBend(index, {
                x: bend.x + step.x,
                y: bend.y + step.y,
              }),
            })
          }
          onRemove={() =>
            editEdge(edgeID, {
              waypoints: bends.filter((_, each) => each !== index),
            })
          }
        />
      ))}
    </EdgeLabelRenderer>
  );
}
